import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { renderApi } from '../services/api'
import { LayoutConfig } from '../types'

export interface Component {
  name_id: string
  name?: string // legado
  component: string
  label: string
  description?: string
  type?: string
  position?: string
  props?: any
  values?: any
  order?: number
  hide?: boolean
  can_order?: boolean  // false = não pode ser ordenado
  can_hide?: boolean   // false = não pode ser ocultado
  can_clone?: boolean  // false = não pode ser clonado
  can_remove?: boolean // false = não pode ser removido
  scope?: 'global' | 'page'
}

interface EditorState {
  // Estado
  components: Component[]
  registry: Component[]
  selectedComponent: string | null
  currentPage: string
  isDirty: boolean
  lastSaved: Date | null
  isLoading: boolean
  isLoadingComponents?: boolean
  isLoadingRegistry?: boolean
  hasLoadedComponents?: boolean
  error: string | null
  previewMode: 'desktop' | 'mobile'
  previewWidth: number | null
  previewVersion: number
  
  // Ações
  initializeComponents: (layoutConfig: LayoutConfig, page: string) => void
  setComponents: (components: Component[]) => void
  selectComponent: (componentName: string | null) => void
  addComponent: (component: Component) => void
  addFromRegistry: (registryIdentifier: string) => void
  removeComponent: (componentName: string) => void
  reorderComponents: (fromIndex: number, toIndex: number) => void
  updateComponent: (componentName: string, updates: Partial<Component>) => void
  updateComponentProp: (componentName: string, propName: string, value: any) => void
  cloneComponent: (componentName: string) => void
  toggleHideComponent: (componentName: string) => void
  markDirty: () => void
  markClean: () => void
  togglePreviewMode: () => void
  setPreviewWidth: (width: number | null) => void
  bumpPreviewVersion: () => void
  applyAndRefreshPreview: (nextComponents: Component[]) => Promise<void>
  
  // Auto-save
  debouncedSave: () => void
  saveChanges: () => Promise<void>
  loadComponents: () => Promise<void>
  loadRegistry: () => Promise<void>
  
  // Reset
  reset: () => void
}

const getComponentId = (component: any): string => {
  const nameId = typeof component?.name_id === 'string' ? component.name_id.trim() : ''
  const legacyName = typeof component?.name === 'string' ? component.name.trim() : ''
  return nameId || legacyName || ''
}
const normalizeComponentForStore = (component: any): Component => {
  const fallbackId = `${component?.component || 'component'}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`
  const resolvedId = getComponentId(component) || fallbackId
  return {
    ...component,
    name_id: resolvedId,
  } as Component
}

export const useEditorStore = create<EditorState>()(
  devtools(
    (set, get) => ({
      // Estado inicial
      components: [],
      registry: [],
      selectedComponent: null,
      currentPage: 'home',
      isDirty: false,
      lastSaved: null,
      isLoading: false,
      isLoadingComponents: false,
      isLoadingRegistry: false,
      hasLoadedComponents: false,
      error: null,
      previewMode: 'desktop',
      previewWidth: null,
      previewVersion: Date.now(),

      // Ações
      initializeComponents: (layoutConfig, page) => {
        console.log(`🔄 initializeComponents chamado com page=${page}, layoutConfig:`, {
          hasLayout: !!layoutConfig?.layout,
          pages: Object.keys(layoutConfig?.layout || {}),
          homeSections: layoutConfig?.layout?.[page]?.sections?.length || 0
        });
        
        // Carregar seções da página e globais
        const pageSections = layoutConfig?.layout?.[page]?.sections?.map((c: any) => normalizeComponentForStore({ ...c, scope: 'page' })) ?? []
        const globalSections = layoutConfig?.layout?.global?.sections?.map((c: any) => normalizeComponentForStore({ ...c, scope: 'global' })) ?? []
        
        const sections = [...globalSections, ...pageSections]
        console.log(`📋 Seções encontradas: Global=${globalSections.length}, Page=${pageSections.length}, Total=${sections.length}`);
        
        // Separar componentes não ordenáveis dos ordenáveis
        const nonSortable = sections.filter((c: any) => c.can_order === false)
        const sortable = sections.filter((c: any) => c.can_order !== false)
        
        // Atribuir ordem apenas aos ordenáveis
        const sortableWithOrder = sortable.map((c: any, index: number) => ({
          ...c,
          order: index,
        }))
        
        // Combinar: não ordenáveis mantêm sua posição, ordenáveis recebem ordem
        const components = [...nonSortable, ...sortableWithOrder]
        
        console.log(`✅ Componentes inicializados: ${components.length} (${nonSortable.length} não ordenáveis, ${sortable.length} ordenáveis)`);
        
        set({
          currentPage: page,
          components,
          isDirty: false,
          isLoading: false,
          hasLoadedComponents: true,
        })
      },

      // Setters
      setComponents: (components) => {
        set({ components: components.map((component: any) => normalizeComponentForStore(component)), isDirty: false })
      },

      selectComponent: (componentName) => {
        set({ selectedComponent: componentName })
      },

      setPreviewWidth: (width) => set({ previewWidth: width }),
      bumpPreviewVersion: () => set({ previewVersion: Date.now() }),

      // Aplica alterações no backend e atualiza o preview (helper centralizado)
      // Usa set/get do escopo do store
      // - Persiste JSON
      // - Força reload imediato do iframe
      // - Aguarda apply() e sincroniza com timestamp do backend
      applyAndRefreshPreview: async (nextComponents: Component[]) => {
        const { currentPage } = get()
        
        // Sanitizar conteúdo em produção para evitar chamadas a localhost
        const sanitizeForProd = (input: any): any => {
          if (input == null) return input
          if (typeof input === 'string') {
            // Remove param theme=local preservando a querystring
            let out = input.replace(/([?&])theme=local(&|$)/g, (m, sep, tail) => (tail === '&' ? sep : ''))
            return out
          }
          if (Array.isArray(input)) {
            return input.map((v) => sanitizeForProd(v))
          }
          if (typeof input === 'object') {
            const out: any = Array.isArray(input) ? [] : { ...input }
            for (const key of Object.keys(input)) {
              out[key] = sanitizeForProd((input as any)[key])
            }
            return out
          }
          return input
        }

        const normalizedComponents = nextComponents.map((component: any) => normalizeComponentForStore(component))
        const componentsToSend = import.meta.env.DEV
          ? normalizedComponents
          : normalizedComponents.map((c) => ({
              ...c,
              values: sanitizeForProd(c.values),
            }))

        // Persistir apenas `name_id`. O fallback de leitura usa `name` quando necessário.
        const serializedComponents = componentsToSend.map((component: any) => {
          const { name, ...rest } = component
          return {
            ...rest,
            name_id: component?.name_id || component?.name,
          }
        })

        // Persistir JSON passando a página atual
        await renderApi.updateEditingJson({ components: serializedComponents, page: currentPage })
        // Aplicar no backend e sincronizar timestamp
        const resp = await renderApi.apply()
        const ts = resp?.timestamp ? Date.parse(resp.timestamp) : Date.now()
        set({ isDirty: false, isLoading: false, lastSaved: new Date(), previewVersion: ts })
      },

      addComponent: (component) => {
        const { components } = get()
        const normalizedIncoming = normalizeComponentForStore(component)
        const isPageScoped = component.scope !== 'global'
        const normalizeInstanceBaseName = (name: string) =>
          name.replace(/_(\d{10,})(?:_[a-z0-9]+)?$/i, '')
        const incomingBaseName = normalizeInstanceBaseName(getComponentId(normalizedIncoming))
        const hasExistingPageInstance =
          normalizedIncoming.can_clone === false &&
          isPageScoped &&
          components.some(
            (existing) =>
              existing.scope !== 'global' &&
              existing.component === normalizedIncoming.component &&
              normalizeInstanceBaseName(getComponentId(existing)) === incomingBaseName,
          )

        if (hasExistingPageInstance) {
          set({
            error: `O componente "${normalizedIncoming.label || normalizedIncoming.component}" só pode ser adicionado uma vez por página.`,
          })
          return
        }
        
        // Separar componentes não ordenáveis dos ordenáveis
        const nonSortable = components.filter(c => c.can_order === false)
        const sortable = components.filter(c => c.can_order !== false)
        
        const newComponent = {
          ...normalizedIncoming,
          order: sortable.length  // Ordem baseada apenas nos ordenáveis
        }
        
        // Combinar: não ordenáveis no início + ordenáveis + novo componente
        const updated = [...nonSortable, ...sortable, newComponent]
        
        set({ 
          components: updated,
          isDirty: true 
        })
        get().debouncedSave()
      },

      removeComponent: (componentName) => {
        const { components } = get()
        const filtered = components.filter(c => getComponentId(c) !== componentName)
        
        // Separar componentes não ordenáveis dos ordenáveis
        const nonSortable = filtered.filter(c => c.can_order === false)
        const sortable = filtered.filter(c => c.can_order !== false)
        
        // Reordenar apenas os ordenáveis
        const reorderedSortable = sortable.map((c, index) => ({ ...c, order: index }))
        
        // Combinar mantendo não ordenáveis no início
        const reordered = [...nonSortable, ...reorderedSortable]
        
        set({ 
          components: reordered,
          isDirty: true 
        })
        get().debouncedSave()
      },

      reorderComponents: (fromIndex, toIndex) => {
        const { components } = get()
        
        // Separar componentes não ordenáveis dos ordenáveis
        const nonSortable = components.filter(c => c.can_order === false)
        const sortable = components.filter(c => c.can_order !== false)
        
        // Reordenar apenas os componentes ordenáveis
        const newSortable = [...sortable]
        const [movedComponent] = newSortable.splice(fromIndex, 1)
        newSortable.splice(toIndex, 0, movedComponent)
        
        // Atualizar ordem dos ordenáveis
        const reorderedSortable = newSortable.map((c, index) => ({ ...c, order: index }))
        
        // Combinar: componentes não ordenáveis no início + ordenáveis reordenados
        const reordered = [...nonSortable, ...reorderedSortable]

        // Atualizar estado imediatamente
        set({ components: reordered, isDirty: true, isLoading: true, error: null })

        // Enviar atualização para o backend e aplicar instantaneamente
        ;(async () => {
          try {
            await get().applyAndRefreshPreview(reordered)
          } catch (error) {
            console.error('❌ Erro ao aplicar reordenação:', error)
            set({ 
              error: error instanceof Error ? error.message : 'Erro ao aplicar reordenação',
              isLoading: false 
            })
          }
        })()
      },

      cloneComponent: (componentName) => {
        const { components } = get()
        const original = components.find(c => getComponentId(c) === componentName)
        if (!original) return
        if (original.can_clone === false) {
          set({
            error: `O componente "${original.label || original.component}" não pode ser clonado.`,
          })
          return
        }

        const timestamp = Date.now()

        const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        // Remove qualquer timestamp(s) existente(s) no final do nome base
        const baseName = getComponentId(original).replace(/_(\d+)(?:_\d+)*$/, '')

        const replaceBaseWithTimestamp = (str: string): string => {
          // Substitui `${baseName}` com timestamp quando seguido por '_' ou final da string,
          // removendo qualquer timestamp anterior para evitar duplicidade
          const baseEsc = escapeRegExp(baseName)
          // Caso: base + (opcional _123) + underscore após
          const patternWithUnderscore = new RegExp(`(^|\\b)${baseEsc}(?:_\\d+)?(?=_)`, 'g')
          let out = str.replace(patternWithUnderscore, `$1${baseName}_${timestamp}`)
          // Caso: base + (opcional _123) no final
          const patternAtEnd = new RegExp(`(^|\\b)${baseEsc}(?:_\\d+)?$`, 'g')
          out = out.replace(patternAtEnd, `$1${baseName}_${timestamp}`)
          return out
        }

        const updateClassesWithTimestamp = (value: any): any => {
          if (value == null) return value
          if (typeof value === 'string') {
            return replaceBaseWithTimestamp(value)
          }
          if (Array.isArray(value)) {
            return value.map(item => updateClassesWithTimestamp(item))
          }
          if (typeof value === 'object') {
            const out: any = Array.isArray(value) ? [] : { ...value }
            for (const key of Object.keys(value)) {
              const v = (value as any)[key]
              // Only mutate class-like fields or traverse objects
              if (key === 'class' || typeof v === 'object' || typeof v === 'string') {
                out[key] = updateClassesWithTimestamp(v)
              } else {
                out[key] = v
              }
            }
            return out
          }
          return value
        }

        const clone: Component = {
          ...original,
          // Garante apenas um timestamp no final do identificador
          name_id: `${baseName}_${timestamp}`,
          order: original.can_order === false ? undefined : components.filter(c => c.can_order !== false).length,
          hide: original.hide ?? false,
          values: updateClassesWithTimestamp(original.values)
        }

        // Separar componentes não ordenáveis dos ordenáveis
        const nonSortable = components.filter(c => c.can_order === false)
        const sortable = components.filter(c => c.can_order !== false)
        
        // Adicionar clone aos ordenáveis ou não ordenáveis conforme apropriado
        const updatedSortable = clone.can_order === false 
          ? sortable 
          : [...sortable, clone]
        const updatedNonSortable = clone.can_order === false 
          ? [...nonSortable, clone] 
          : nonSortable
        
        // Reordenar apenas os ordenáveis
        const reorderedSortable = updatedSortable.map((c, index) => ({ ...c, order: index }))
        
        // Combinar mantendo não ordenáveis no início
        const updated = [...updatedNonSortable, ...reorderedSortable]

        set({ components: updated, isDirty: true, isLoading: true, error: null })

        ;(async () => {
          try {
            await get().applyAndRefreshPreview(updated)
          } catch (error) {
            console.error('❌ Erro ao clonar componente:', error)
            set({ 
              error: error instanceof Error ? error.message : 'Erro ao clonar componente',
              isLoading: false 
            })
          }
        })()
      },

      toggleHideComponent: (componentName) => {
        const { components } = get()
        
        // Alternar hide mantendo a estrutura de ordenação
        const toggled = components.map(c => 
          getComponentId(c) === componentName ? { ...c, hide: !c.hide } : c
        )
        
        // Separar componentes não ordenáveis dos ordenáveis
        const nonSortable = toggled.filter(c => c.can_order === false)
        const sortable = toggled.filter(c => c.can_order !== false)
        
        // Reordenar apenas os ordenáveis
        const reorderedSortable = sortable.map((c, index) => ({ ...c, order: index }))
        
        // Combinar mantendo não ordenáveis no início
        const updated = [...nonSortable, ...reorderedSortable]

        set({ components: updated, isDirty: true, isLoading: true, error: null })

        ;(async () => {
          try {
            await get().applyAndRefreshPreview(updated)
          } catch (error) {
            console.error('❌ Erro ao alterar visibilidade:', error)
            set({ 
              error: error instanceof Error ? error.message : 'Erro ao alterar visibilidade',
              isLoading: false 
            })
          }
        })()
      },

      updateComponent: (componentName, updates) => {
        const { components } = get()
        const updated = components.map(c => 
          getComponentId(c) === componentName ? { ...c, ...updates } : c
        )
        set({ 
          components: updated,
          isDirty: true 
        })
        get().debouncedSave()
      },

      updateComponentProp: (componentName, propName, incoming) => {
        
        const { components, previewMode } = get()
        
        const convertStylesForPreview = (obj: any, existing?: any): any => {
          if (!obj || typeof obj !== 'object') return obj
          if (Array.isArray(obj)) {
            return obj.map((item, idx) => convertStylesForPreview(item, existing && existing[idx]))
          }
          const out: any = { ...obj }
          for (const key of Object.keys(obj)) {
            const val = obj[key]
            const prev = existing ? existing[key] : undefined
            if (val && typeof val === 'object') {
              if (Object.prototype.hasOwnProperty.call(val, 'style')) {
                const styleVal = (val as any).style
                const base = typeof (prev?.style) === 'string' ? { desktop: prev.style, mobile: '' } : (prev?.style || {})
                if (typeof styleVal === 'string') {
                  out[key] = {
                    ...(val as any),
                    style: { ...base, [previewMode]: styleVal }
                  }
                } else if (styleVal && typeof styleVal === 'object') {
                  out[key] = {
                    ...(val as any),
                    style: { ...base, ...styleVal }
                  }
                }
              } else {
                out[key] = convertStylesForPreview(val, prev)
              }
            }
          }
          return out
        }

        const updated = components.map(c => {
          if (getComponentId(c) !== componentName) return c

          const nextValueContainer = { ...(c.values || {}) }
          const currentProp = nextValueContainer[propName] || {}

          // Detectar o tipo do campo a partir do schema de props do componente
          const fieldType: string | undefined =
            (c as any)?.props && typeof (c as any).props === 'object'
              ? (c as any).props[propName]?.type
              : undefined

          // Normalização: se incoming vier como { value } + extras, achatar quando possível
          let normalized: any
          if (incoming && typeof incoming === 'object' && Object.prototype.hasOwnProperty.call(incoming, 'value')) {
            const { value, ...rest } = incoming
            // Para campos select/date, SEMPRE preservar o objeto completo (mais quaisquer metadados como timestamp).
            if (fieldType === 'select' || fieldType === 'date') {
              normalized = { ...rest, value }
            } else {
              // Comportamento anterior (retrocompatibilidade): se só há "value", usar valor simples.
              normalized = Object.keys(rest).length === 0 ? value : { ...rest, value }
            }
          } else {
            normalized = incoming
          }

          if (normalized && typeof normalized === 'object' && Object.prototype.hasOwnProperty.call(normalized, 'style')) {
            const styleVal = (normalized as any).style
            const existingStyle = currentProp?.style
            const base = typeof existingStyle === 'string' 
              ? { desktop: existingStyle, mobile: '' } 
              : (existingStyle && typeof existingStyle === 'object' ? existingStyle : {})

            if (typeof styleVal === 'string') {
              nextValueContainer[propName] = {
                ...currentProp,
                ...normalized,
                style: {
                  ...base,
                  [previewMode]: styleVal
                }
              }
            } else if (styleVal && typeof styleVal === 'object') {
              nextValueContainer[propName] = {
                ...currentProp,
                ...normalized,
                style: {
                  ...base,
                  ...styleVal
                }
              }
            } else {
              nextValueContainer[propName] = normalized
            }
          } else {
            // Para arrays/objetos aninhados, converter recursivamente quaisquer style strings
            const converted = convertStylesForPreview(normalized, currentProp)
            nextValueContainer[propName] = converted
          }

          return {
            ...c,
            values: nextValueContainer
          }
        })
        
        set({ 
          components: updated,
          isDirty: true 
        })
        // Salvamento somente ao clicar em "Aplicar"
      },

      markDirty: () => set({ isDirty: true }),
      markClean: () => set({ isDirty: false }),
      togglePreviewMode: () => {
        const { previewMode } = get()
        set({ previewMode: previewMode === 'desktop' ? 'mobile' : 'desktop' })
      },

      // Auto-save com debounce
      debouncedSave: (() => {
        let timeoutId: NodeJS.Timeout | null = null
        return () => {
          if (timeoutId) clearTimeout(timeoutId)
          timeoutId = setTimeout(() => {
            get().saveChanges()
          }, 2000) // 2 segundos de debounce
        }
      })(),

      saveChanges: async () => {
        const { components, isDirty } = get()
        if (!isDirty || components.length === 0) return

        set({ isLoading: true, error: null })

        try {
          await get().applyAndRefreshPreview(components)
          set({ isLoading: false })
          console.log('✅ Mudanças salvas e aplicadas')
        } catch (error) {
          console.error('❌ Erro ao salvar mudanças:', error)
          set({ 
            error: error instanceof Error ? error.message : 'Erro ao salvar',
            isLoading: false 
          })
        }
      },

      loadComponents: async () => {
        const { isLoadingComponents, hasLoadedComponents, currentPage } = get()
        if (isLoadingComponents || hasLoadedComponents) return
        set({ isLoadingComponents: true, error: null })

        try {
          const response = await renderApi.getComponents(currentPage)
          if (response.success) {
            // Separar componentes não ordenáveis dos ordenáveis
            const normalized = response.components.map((component: any) => normalizeComponentForStore(component))
            const nonSortable = normalized.filter((c: any) => c.can_order === false)
            const sortable = normalized.filter((c: any) => c.can_order !== false)
            
            // Atribuir ordem apenas aos ordenáveis
            const sortableWithOrder = sortable.map((c: any, index: number) => ({
              ...c,
              order: index,
            }))
            
            // Combinar: não ordenáveis mantêm sua posição, ordenáveis recebem ordem
            const components = [...nonSortable, ...sortableWithOrder]
            
            set({ 
              components,
              isDirty: false,
              isLoadingComponents: false,
              hasLoadedComponents: true
            })
          } else {
            set({ isLoadingComponents: false })
          }
        } catch (error) {
          console.error('❌ Erro ao carregar componentes:', error)
          set({ 
            error: error instanceof Error ? error.message : 'Erro ao carregar',
            isLoadingComponents: false 
          })
        }
      },


      loadRegistry: async () => {
        const { isLoadingRegistry } = get()
        if (isLoadingRegistry) return
        set({ isLoadingRegistry: true, error: null })

        try {
          const response = await renderApi.getRegistry()
          if (response.registry) {
            set({ 
              registry: response.registry.map((component: any) => normalizeComponentForStore(component)),
              isLoadingRegistry: false
            })
          } else {
            set({ isLoadingRegistry: false })
          }
        } catch (error) {
          console.error('❌ Erro ao carregar registry:', error)
          set({ 
            // Não bloquear o editor se o registry falhar
            isLoadingRegistry: false 
          })
        }
      },

      addFromRegistry: (registryIdentifier: string) => {
        const { registry, components } = get()
        // O identificador real da instância no editor é o "name".
        // Mantemos fallback por "component" para retrocompatibilidade.
        const def =
          registry.find((c) => c.name_id === registryIdentifier) ||
          registry.find((c) => c.name === registryIdentifier) ||
          registry.find((c) => c.component === registryIdentifier)
        
        if (!def) {
          console.error(`Componente ${registryIdentifier} não encontrado no registry`)
          return
        }

        const normalizeInstanceBaseName = (name: string) =>
          name.replace(/_(\d{10,})(?:_[a-z0-9]+)?$/i, '')
        const registryBaseName = normalizeInstanceBaseName(def.name_id || def.name || def.label || def.component)

        if (
          def.can_clone === false &&
          def.scope !== 'global' &&
          components.some(
            (component) =>
              component.scope !== 'global' &&
              component.component === def.component &&
              normalizeInstanceBaseName(getComponentId(component)) === registryBaseName,
          )
        ) {
          set({
            error: `O componente "${registryBaseName}" só pode ser adicionado uma vez por página.`,
          })
          return
        }

        // Gerar ID aleatório (timestamp + random) para garantir unicidade
        // apenas para componentes que podem ter múltiplas instâncias.
        const randomId = Math.random().toString(36).substring(2, 9);
        const timestamp = Date.now();
        const uniqueId = `${timestamp}_${randomId}`;
        const baseName = def.name_id || def.name || def.label || def.component
        const instanceName = def.can_clone === false ? baseName : `${baseName}_${uniqueId}`
        
        // Criar nova instância
        const newComponent: Component = {
          ...def,
          // O name da instância é composta pelo name do registry + identificador único
          // Ex: "Banner Principal_1715..."
          name_id: instanceName,
          // O componente deve manter o name original como label inicial (ou o label do registry)
          label: baseName,
          values: {}, 
        }
        
        // Adicionar usando a lógica padrão
        get().addComponent(newComponent)
      },

      reset: () => {
        set({
          components: [],
          registry: [],
          isDirty: false,
          lastSaved: null,
          isLoading: false,
          error: null,
          previewMode: 'desktop',
          previewWidth: null,
        })
        get().bumpPreviewVersion()
      }
    }),
    {
      name: 'editor-store'
    }
  )
)
